<template>
  <section>
    <div v-if="!hideHeader" class="d-flex flex-row align-items-baseline">
      <slot name="group-header" :group="group">
        <h2 class="font-weight-bold text-dark product-group-name mr-2">{{ group.name }}</h2>
      </slot>
      <transition v-if="!alwaysShowAllProducts && showControls" appear name="fade">
        <button
          v-if="!showAllProducts"
          class="btn product-group-style-toggle --show font-weight-bold text-primary h4"
          @click="showAllProducts = true"
        >
          {{ $t('dp.generic.showAll') }}
        </button>
        <button v-else class="btn product-group-style-toggle --hide font-weight-bold text-primary h4" @click="showAllProducts = false">
          {{ $t('dp.generic.showLess') }}
        </button>
      </transition>
    </div>
    <div class="d-flex flex-row product-group-style-wrapper align-items-center mw-100">
      <transition name="fade">
        <button
          v-show="!maxScrollLeft && !showAllProducts"
          aria-label="Previous Item"
          class="product-group-style-control previous-button --left h-100"
          :aria-controls="encodeURIComponent(group.name.replace(/\s+/g, '-').toLowerCase())"
          @click="prev(windowWidth >= 991 ? 400 : 300)"
        >
          <fa-icon :icon="['fas', 'chevron-left']" size="2x" aria-label="Scroll Left" class="prev" />
        </button>
      </transition>
      <transition :name="showAllProducts ? '' : 'fade'">
        <button
          v-show="!showAllProducts && showControls && !maxScrollRight"
          aria-label="Next Item"
          class="product-group-style-control next-button --right h-100"
          :aria-controls="encodeURIComponent(group.name.replace(/\s+/g, '-').toLowerCase())"
          @click="next(windowWidth >= 991 ? 400 : 300)"
        >
          <fa-icon :icon="['fas', 'chevron-right']" size="2x" aria-label="Scroll Right" class="next" />
        </button>
      </transition>
      <b-col
        :ref="`stylesSlider${group.id}`"
        cols="auto"
        class="d-flex align-items-stretch product-group-style-row px-0"
        :class="showAllProducts ? 'flex-wrap justify-content-around justify-items-center' : 'flex-nowrap'"
        aria-label="Carousel Slides"
        role="region"
      >
        <b-col
          v-for="style in group.productStyles"
          :key="style.id"
          :cols="showAllProducts ? 12 : 12 / calcColumnsInSlider"
          md="3-5"
          class="d-flex flex-column product-group-style-col px-0 pr-1"
          aria-roledescription="slide"
          role="group"
        >
          <div class="product-group-style-col-row">
            <b-link :to="routeToStyle(style)" class="d-flex flex-column align-items-stretch text-wrap text-center">
              <div class="product-group-style-img-wrapper">
                <img
                  v-lazy="{
                    src: style.image && style.image.thumbnail ? getImage(style.image.thumbnail) : null,
                    error: require(`@/assets/images/${$t('essentials.placeholderImage')}`),
                    loading: require(`@/assets/images/${$t('essentials.placeholderImage')}`),
                  }"
                  alt=""
                  :src="require(`@/assets/images/${$t('essentials.placeholderImage')}`)"
                  width="85%"
                  class="img mx-auto"
                />
              </div>
              <div :class="{ 'flex-grow-1': !isClickToActionItem }" class="d-flex flex-column product-group-style-info mb-0 pb-0 mt-2 pt-2">
                <p :class="!isClickToActionItem ? 'text-dark' : 'text-black'" class="product-group-style-info-name h4">
                  {{ style.name }}
                </p>
              </div>
            </b-link>
            <div class="text-center">
              <p
                v-if="style.priceRange && style.priceRange.min !== style.priceRange.max"
                class="product-group-style-info-price text-dark font-weight-bold pb-2 mb-2 mt-1 h3"
              >
                <span> {{ $n(style.priceRange.min, 'currency') }} - {{ $n(style.priceRange.max, 'currency') }} </span>
              </p>
              <p v-else class="product-group-style-info-price text-dark font-weight-bold pb-2 mb-2 mt-1 h3">
                <span
                  :class="{ 'text-muted-alt font-weight-normal': style.discountedPrice }"
                  :style="{ textDecoration: style.discountedPrice ? 'line-through' : '' }"
                >
                  {{ $n(style.price || group.price, 'currency') }}
                </span>
                <span v-if="style.discountedPrice" class="ml-1">
                  <span class="sr-only">{{ $t('dp.generic.priceReducedTo') }} </span>{{ $n(style.discountedPrice, 'currency') }}
                </span>
              </p>
            </div>
            <div v-if="isClickToActionItem" class="mb-2 d-flex flex-column justify-content-between flex-grow-1 w-100 text-wrap">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div v-if="style.description" class="text-left px-3 text-dark" v-html="shortenDescription(style.description)" />
            </div>

            <div v-if="isClickToActionItem">
              <b-button
                v-if="!style.hasMultipleSKUs"
                :disabled="!ctaProductInStock(style)"
                block
                class="product-group-style-action-link"
                variant="primary"
                @click.prevent="addStyleToCart(style)"
              >
                {{ $t(ctaProductInStock(style) ? 'productInterrupt.actionButton.default' : 'productInterrupt.actionButton.oos') }}
                <div v-if="showAddToCartLoading(style)" role="status" class="fa-pulse float-right mt-1">
                  <fa-icon :icon="['fas', 'spinner']" class="d-flex" />
                  <span class="sr-only">{{ $t('dp.buttons.loading') }}</span>
                </div>
              </b-button>
              <b-link
                v-else-if="style.hasMultipleSKUs && actionLinkText"
                :aria-label="`${actionLinkText} ${style.name}`"
                :to="routeToStyle(style)"
                class="product-group-style-action-link btn-primary btn-block text-center"
              >
                {{ actionLinkText }}
              </b-link>
            </div>
          </div>
        </b-col>
      </b-col>
    </div>
  </section>
</template>

<script>
import getImage from '@/mixins/getImage';
import { EssentialsAddToBasketMixins } from '@/mixins/components/addToBasket';
import { ProductStylesMixins } from '@/mixins/components/productStyles';
import { mapState } from 'vuex';

export default {
  name: 'EssentialsProductStyles',
  mixins: [getImage, EssentialsAddToBasketMixins, ProductStylesMixins],
  props: {
    group: {
      type: Object,
      required: true,
    },
    petId: {
      type: [String, Number],
      default: '',
      required: true,
    },
    alwaysShowAllProducts: {
      type: Boolean,
      default: false,
      required: false,
    },
    actionLinkText: {
      type: String,
      default: null,
      required: false,
    },
    hideHeader: {
      type: Boolean,
      default: false,
      required: false,
    },
    isClickToActionItem: {
      type: Boolean,
      default: false,
      required: false,
    },
    redirectPath: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      showAllProducts: this.alwaysShowAllProducts,
      maxScrollLeft: true,
      maxScrollRight: false,
      mouseHovering: false,
      windowWidth: window.innerWidth,
    };
  },
  computed: {
    ...mapState('order', ['newItemsAddition']),
    showControls() {
      return this.group?.productStyles?.length > this.calcColumnsInSlider;
    },
    calcColumnsInSlider() {
      if (this.windowWidth > 991) {
        // If window width is greater than 991px, it will show 5 columns
        return 5;
      } else if (this.windowWidth >= 764) {
        // If window width is greater than 764px, it will show 4 columns
        return 3;
      } else {
        // If window width is less than 764px, it will show 2 columns
        return 2;
      }
    },
  },
  updated() {
    if (this.alwaysShowAllProducts) {
      this.showAllProducts = this.alwaysShowAllProducts;
    }
  },
  mounted() {
    window.addEventListener('resize', this.onWindowResize);
    this.$refs[`stylesSlider${this.group?.id}`]?.addEventListener('scroll', this.showHideCarets);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
  },
  methods: {
    showHideCarets() {
      const stylesSlider = this.$refs[`stylesSlider${this.group?.id}`];
      if (stylesSlider.scrollWidth - stylesSlider.clientWidth === stylesSlider.scrollLeft && stylesSlider.scrollLeft !== 0) {
        this.maxScrollLeft = false;
        this.maxScrollRight = true;
      } else if (stylesSlider.scrollLeft === 0) {
        this.maxScrollLeft = true;
        this.maxScrollRight = false;
      } else if (stylesSlider.scrollWidth - stylesSlider.clientWidth !== 0) {
        this.maxScrollLeft = false;
        this.maxScrollRight = false;
      }
    },
    prev(scrollLeftValue = 200) {
      this.$nextTick(() => {
        const elem = this.$refs[`stylesSlider${this.group?.id}`];
        if (elem.scrollLeft - scrollLeftValue < scrollLeftValue) {
          scrollLeftValue *= 2;
        }
        elem?.scrollBy({
          left: -scrollLeftValue,
          behavior: 'smooth',
        });
        this.showHideCarets();
      });
    },
    next(scrollLeftValue = 200) {
      this.$nextTick(() => {
        const elem = this.$refs[`stylesSlider${this.group?.id}`];
        if (elem.scrollWidth - elem.clientWidth - (elem.scrollLeft + scrollLeftValue) <= scrollLeftValue) {
          scrollLeftValue *= 2;
        }
        elem?.scrollBy({
          left: scrollLeftValue,
          behavior: 'smooth',
        });
        this.showHideCarets();
      });
    },
    onWindowResize() {
      this.windowWidth = window.innerWidth;
      this.$nextTick(() => {
        this.showHideCarets();
      });
    },
    addStyleToCart(style) {
      if (this.showAddToCartLoading(style)) {
        return;
      }
      this.selectedProduct = style?.products?.[0];
      if (!this.selectedProduct || this.selectedProduct?.status !== 'I') {
        return;
      }
      this.addToBasket()
        .then(() => {
          this.$emit('showSideBasket');
        })
        .catch(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        });
    },
    getProductGroupId(style) {
      if (!this.isClickToActionItem || !style) {
        return this.group.id;
      }

      if (style?.productGroups?.length) {
        for (let index = style?.productGroups.length - 1; index >= 0; index--) {
          const { doNotDisplayInStore, status, id } = style.productGroups[index];
          if (!doNotDisplayInStore && status === 'P') {
            return id;
          }
        }
      }
      return this.group.id;
    },
    ctaProductInStock(style) {
      return style?.products?.[0]?.status === 'I';
    },
    routeToStyle(style) {
      if (!style?.id || !this.getProductGroupId(style)) {
        return;
      }
      const query = { ...this.$router.currentRoute.query };
      if (this.redirectPath) {
        query.redirect_path = this.redirectPath;
      }
      return {
        name: 'EssentialStyle',
        params: { styleId: style.id, groupId: this.getProductGroupId(style) },
        query,
      };
    },
    productIdFromStyle(style) {
      return style?.products?.[0]?.id;
    },
    showAddToCartLoading(style) {
      return this.newItemsAddition?.includes(this.productIdFromStyle(style));
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@docupet/component-library/src/assets/styles/_variables';
.product-group-style-wrapper {
  position: relative;
  .product-group-style-control {
    z-index: 1;
    position: absolute;
    display: inline-flex;
    align-items: center;
    width: 2rem;
    justify-content: center;
    margin-bottom: 0.25rem;
    background-color: transparent;
    &.--left {
      left: 0;
      box-shadow: 10px 0 20px 0 #f8f8f8;
      background-image: linear-gradient(to left, rgba(#f8f8f8, 0.85), rgba(#f8f8f8, 1));
    }
    &.--right {
      left: calc(100% - 2rem);
      color: $dp-navy;
      box-shadow: -10px 0 20px 0 #f8f8f8;
      background-image: linear-gradient(to right, rgba(#f8f8f8, 0.85), rgba(#f8f8f8, 1));
    }
    @media (max-width: 991px) {
      width: 1.5rem;
      &.--right {
        left: calc(100% - 1.5rem);
      }
    }
  }
  .product-group-style-toggle {
    z-index: 1;
    position: absolute;
    display: inline-flex;
    top: calc(100% - 25px);
    left: calc(100% - 200px);
    text-decoration: underline;
    @media (max-width: 764px) {
      left: 70%;
    }
  }
  .product-group-style-row {
    width: 100%;
    overflow-y: hidden;
    white-space: nowrap;
    scroll-snap-type: both proximity;
    transition: all 0.2s ease 0.3s;
    -o-transition: all 0.2s ease 0.3s;
    -moz-transition: all 0.2s ease 0.3s;
    -webkit-transition: all 0.2s ease 0.3s;
    &::after {
      content: '';
      flex: auto;
    }
    &::-webkit-scrollbar {
      height: 0 !important;
    }
    &::-moz-horizontal-scrollbar {
      height: 0 !important;
    }
    &.flex-wrap {
      row-gap: 0.3rem;
      overflow-x: hidden;
      @supports (overflow-x: clip) {
        overflow-x: clip;
      }
      justify-items: center;
      @media (max-width: 991px) {
        display: grid !important;
      }
      @media (min-width: 764px) and (max-width: 991px) {
        grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      }
      @media (max-width: 764px) {
        grid-template-columns: repeat(auto-fill, minmax(50%, 1fr));
      }
    }
    .product-group-style-col {
      min-width: 100px;
      &:last-child {
        margin-right: 0;
        padding-right: 0;
      }
      @media (min-width: 992px) {
        &.col-md-3-5 {
          flex: 0 0 20%;
          max-width: 20%;
        }
      }
      .product-group-style-col-row {
        cursor: pointer;
        background-color: $dp-gray-1;
        overflow-x: hidden;
        outline-offset: -1px;
        @supports (overflow-x: clip) {
          overflow-x: clip;
        }
        height: 100%;
        .product-group-style-img-wrapper {
          width: 100%;
        }
        .img {
          object-fit: cover;
          width: 100%;
          width: -moz-available;
          width: -webkit-fill-available;
          width: fill-available;
        }
        @media (max-width: 991px) {
          img {
            min-height: 75px;
          }
          .product-group-style-img-wrapper {
            min-height: 80px;
          }
        }
      }
    }
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.text-black {
  color: $dp-black;
}
.previous-button,
.next-button {
  border: none;
}
</style>
